/**
 * Listing Scoring System for Landing Page Carousel
 * 
 * This module provides a comprehensive scoring system to rank listings based on:
 * - Quality signals (ratings, reviews, completeness)
 * - Seasonal relevance (e.g., ski in winter, hiking in summer)
 * - Diversity (discipline, region variety)
 * - Provider quality (no near-identical offerings from same provider)
 * - Availability (bookable slots in near future)
 */

import { LISTING_SEASONALITY } from '../constants/listingAttributes';

// Scoring weights
const WEIGHTS = {
  RATING: 15,                    // High rating boost
  REVIEW_COUNT: 10,              // More reviews = more trusted
  SEASONAL_RELEVANCE: 20,        // Strong boost for seasonal activities
  AVAILABILITY: 15,              // Has upcoming bookable slots
  LISTING_COMPLETENESS: 10,      // Good description, images, etc.
  RECENCY: 5,                    // Recently created (but not primary)
  PROVIDER_REPUTATION: 10,       // Provider has multiple good listings
  IMAGE_QUALITY: 5,              // Has good quality images
  REGION_DIVERSITY: 10,          // Bonus for underrepresented regions
};

// Diversity caps
const DIVERSITY_CAPS = {
  MAX_PER_DISCIPLINE: 8,         // Max 8 per discipline (ski, hiking, etc.) - increased for 48 listings
  MAX_PER_REGION: 6,             // Max 6 per region - increased for 48 listings
  MAX_PER_PROVIDER: 3,           // Max 3 from same provider - increased for 48 listings
};

/**
 * Discipline to Season Mapping
 * Maps each discipline to the seasons when it's most relevant
 * Seasons: 'winter' | 'spring' | 'summer' | 'autumn' | 'year-round'
 */
const DISCIPLINE_SEASONS = {
  // Skiing - Winter sports (peak: Dec-Mar, prep from Nov)
  alpineSkiing: ['winter', 'autumn'], // autumn for early ski lessons from November
  freerideSkiing: ['winter'],
  freestyleSkiing: ['winter'],
  heliSkiing: ['winter'],
  skiTouring: ['winter'],
  telemarkSkiing: ['winter'],
  crossCountrySkiing: ['winter'],
  grassSkiing: ['summer', 'autumn'], // Grass skiing is summer sport
  
  // Snowboarding - Winter sports
  onPisteSnowboarding: ['winter', 'autumn'], // autumn for early lessons
  freerideSnowboarding: ['winter'],
  freestyleSnowboarding: ['winter'],
  heliBoarding: ['winter'],
  splitBoarding: ['winter'],
  
  // Hiking - Multi-season (peak: summer/autumn)
  mountainHiking: ['spring', 'summer', 'autumn'],
  snowShoeing: ['winter'], // Winter hiking
  
  // Running - Multi-season
  trailRunning: ['spring', 'summer', 'autumn'],
  
  // Horse riding - Year-round
  horseRiding: ['year-round'],
  
  // Biking - Multi-season (peak: spring-autumn)
  mountainBiking: ['spring', 'summer', 'autumn'],
  roadCycling: ['spring', 'summer', 'autumn'],
  eBiking: ['spring', 'summer', 'autumn'],
  fatBiking: ['winter', 'autumn'], // Fat bikes often used in snow
  
  // Climbing - Multi-season
  mountaineering: ['summer', 'autumn'], // High altitude, summer season
  rockClimbing: ['spring', 'summer', 'autumn'],
  viaFerrata: ['spring', 'summer', 'autumn'],
  bouldering: ['year-round'], // Often indoor too
  iceClimbing: ['winter'],
  
  // Sledging - Winter sports
  tobogganing: ['winter'],
  huskySledding: ['winter'],
  horseCarriageSledding: ['winter'],
  snowMobiling: ['winter'],
  
  // River sports - Summer (warm weather + water flow)
  rafting: ['spring', 'summer'], // Spring for snowmelt, summer for warmth
  canyoning: ['summer'], // Peak season when water is warm
  
  // Lake & sea sports - Warm weather
  kayaking: ['spring', 'summer', 'autumn'],
  sailing: ['spring', 'summer', 'autumn'],
  standUpPaddle: ['summer', 'autumn'],
  fishing: ['year-round'], // Fishing all seasons
  boatCruise: ['spring', 'summer', 'autumn'],
  
  // Air sports - Good weather (spring-autumn)
  paragliding: ['spring', 'summer', 'autumn'],
  bungeeJumping: ['spring', 'summer', 'autumn'],
  skydiving: ['spring', 'summer', 'autumn'],
  kiting: ['spring', 'summer', 'autumn'],
  tandemSpeedFlying: ['spring', 'summer', 'autumn'],
  
  // Other experiences - Variable
  avalancheTraining: ['winter'], // Winter safety training
  guidedWalking: ['year-round'],
  culinaryTasting: ['year-round'],
  northernLights: ['winter'], // Best visibility in winter
  yoga: ['year-round'],
  
  // Court sports - Year-round (often indoor)
  tennis: ['year-round'],
  padel: ['year-round'],
};

/**
 * Get current season based on month
 * @returns {string} - 'winter' | 'spring' | 'summer' | 'autumn'
 */
export const getCurrentSeason = () => {
  const month = new Date().getMonth(); // 0-11
  
  // Northern hemisphere seasons (Europe)
  if (month >= 2 && month <= 4) return 'spring';      // Mar-May
  if (month >= 5 && month <= 7) return 'summer';      // Jun-Aug
  if (month >= 8 && month <= 10) return 'autumn';     // Sep-Nov
  return 'winter';                                     // Dec-Feb
};

/**
 * Get relevant seasonality tags for current month
 * Includes current season plus next season (for early bookings)
 */
export const getRelevantSeasons = () => {
  const month = new Date().getMonth();
  const currentSeason = getCurrentSeason();
  
  // Add lookahead for upcoming season (e.g., show ski lessons from Nov for winter)
  const seasonMap = {
    winter: [LISTING_SEASONALITY.WINTER, LISTING_SEASONALITY.YEAR_ROUND],
    spring: [LISTING_SEASONALITY.SPRING, LISTING_SEASONALITY.YEAR_ROUND],
    summer: [LISTING_SEASONALITY.SUMMER, LISTING_SEASONALITY.YEAR_ROUND],
    autumn: [LISTING_SEASONALITY.AUTUMN, LISTING_SEASONALITY.YEAR_ROUND, LISTING_SEASONALITY.WINTER], // Early winter prep
  };
  
  // In November, add winter (early season booking)
  if (month === 10) { // November
    return [LISTING_SEASONALITY.AUTUMN, LISTING_SEASONALITY.WINTER, LISTING_SEASONALITY.YEAR_ROUND];
  }
  
  return seasonMap[currentSeason] || [LISTING_SEASONALITY.YEAR_ROUND];
};

/**
 * Calculate seasonal relevance score based on discipline
 * @param {Object} listing - Listing object
 * @returns {number} - Score from 0 to WEIGHTS.SEASONAL_RELEVANCE
 */
export const calculateSeasonalScore = (listing) => {
  const publicData = listing?.attributes?.publicData || {};
  const { discipline } = publicData;
  
  if (!discipline) return 0;
  
  const currentSeason = getCurrentSeason();
  const disciplineSeasons = DISCIPLINE_SEASONS[discipline];
  
  if (!disciplineSeasons) {
    // If discipline not mapped, give neutral score
    return WEIGHTS.SEASONAL_RELEVANCE * 0.5;
  }
  
  // Year-round activities get good score always
  if (disciplineSeasons.includes('year-round')) {
    return WEIGHTS.SEASONAL_RELEVANCE * 0.7;
  }
  
  // Perfect match for current season
  if (disciplineSeasons.includes(currentSeason)) {
    return WEIGHTS.SEASONAL_RELEVANCE;
  }
  
  // No match - off-season
  return WEIGHTS.SEASONAL_RELEVANCE * 0.2;
};

/**
 * Calculate rating and review score
 * @param {Object} listing - Listing object
 * @returns {number} - Score from 0 to (WEIGHTS.RATING + WEIGHTS.REVIEW_COUNT)
 */
export const calculateRatingScore = (listing) => {
  const author = listing?.author;
  const metadata = author?.attributes?.profile?.metadata || {};
  const { averageRating = 0, ratingCount = 0 } = metadata;
  
  let score = 0;
  
  // Rating score (0-5 stars normalized)
  if (averageRating > 0) {
    score += (averageRating / 5) * WEIGHTS.RATING;
  }
  
  // Review count score (logarithmic scale, maxes out around 20 reviews)
  if (ratingCount > 0) {
    const reviewScore = Math.min(Math.log10(ratingCount + 1) / Math.log10(21), 1);
    score += reviewScore * WEIGHTS.REVIEW_COUNT;
  }
  
  return score;
};

/**
 * Calculate listing completeness score
 * @param {Object} listing - Listing object
 * @returns {number} - Score from 0 to WEIGHTS.LISTING_COMPLETENESS
 */
export const calculateCompletenessScore = (listing) => {
  const attributes = listing?.attributes || {};
  const { title, description, descriptionLength, publicData } = attributes;
  const images = listing?.images || [];
  const imagesCount = listing?.imagesCount || images.length;
  
  let score = 0;
  const maxScore = WEIGHTS.LISTING_COMPLETENESS;
  
  // Has good title (>20 chars)
  if (title && title.length > 20) score += maxScore * 0.2;
  
  // Has substantial description (>100 chars)
  // Use descriptionLength if available (slim SSR format), otherwise use full description
  const descLength = descriptionLength || description?.length || 0;
  if (descLength > 100) score += maxScore * 0.3;
  
  // Has multiple images (use imagesCount from slim format if available)
  if (imagesCount >= 3) score += maxScore * 0.3;
  else if (imagesCount >= 1) score += maxScore * 0.15;
  
  // Has location data
  if (publicData?.city || publicData?.region) score += maxScore * 0.2;
  
  return score;
};

/**
 * Calculate availability score based on upcoming sessions/periods
 * Note: This requires additional API call or data to be passed in
 * @param {Object} listing - Listing object
 * @param {Array} upcomingSessions - Array of upcoming sessions (optional)
 * @returns {number} - Score from 0 to WEIGHTS.AVAILABILITY
 */
export const calculateAvailabilityScore = (listing, upcomingSessions = []) => {
  // If we have session data, use it
  if (upcomingSessions && upcomingSessions.length > 0) {
    const now = new Date();
    const next60Days = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);
    
    const availableInNext60Days = upcomingSessions.filter(session => {
      const sessionDate = new Date(session.startsAt);
      return sessionDate >= now && sessionDate <= next60Days;
    });
    
    if (availableInNext60Days.length > 0) {
      return WEIGHTS.AVAILABILITY;
    }
  }
  
  // Fallback: check if listing is published and not deleted
  const state = listing?.attributes?.state;
  const deleted = listing?.attributes?.deleted;
  
  if (state === 'published' && !deleted) {
    return WEIGHTS.AVAILABILITY * 0.7; // Assume available
  }
  
  return 0;
};

/**
 * Calculate recency score (newer listings get slight boost)
 * @param {Object} listing - Listing object
 * @returns {number} - Score from 0 to WEIGHTS.RECENCY
 */
export const calculateRecencyScore = (listing) => {
  const createdAt = listing?.attributes?.createdAt;
  
  if (!createdAt) return 0;
  
  const now = new Date();
  const created = new Date(createdAt);
  const daysSinceCreated = (now - created) / (1000 * 60 * 60 * 24);
  
  // Listings less than 90 days old get bonus
  if (daysSinceCreated < 90) {
    return WEIGHTS.RECENCY * (1 - daysSinceCreated / 90);
  }
  
  return 0;
};

/**
 * Calculate image quality score
 * @param {Object} listing - Listing object
 * @returns {number} - Score from 0 to WEIGHTS.IMAGE_QUALITY
 */
export const calculateImageScore = (listing) => {
  const images = listing?.images || [];
  
  if (images.length === 0) return 0;
  if (images.length >= 5) return WEIGHTS.IMAGE_QUALITY;
  if (images.length >= 3) return WEIGHTS.IMAGE_QUALITY * 0.7;
  
  return WEIGHTS.IMAGE_QUALITY * 0.4;
};

/**
 * Calculate total score for a listing
 * @param {Object} listing - Listing object
 * @param {Array} upcomingSessions - Array of upcoming sessions (optional)
 * @returns {number} - Total score
 */
export const calculateListingScore = (listing, upcomingSessions = []) => {
  let totalScore = 0;
  
  totalScore += calculateSeasonalScore(listing);
  totalScore += calculateRatingScore(listing);
  totalScore += calculateCompletenessScore(listing);
  totalScore += calculateAvailabilityScore(listing, upcomingSessions);
  totalScore += calculateRecencyScore(listing);
  totalScore += calculateImageScore(listing);
  
  return totalScore;
};

/**
 * Apply diversity caps and filters to listings
 * @param {Array} listings - Array of listings with scores
 * @returns {Array} - Filtered and diverse listings
 */
export const applyDiversityCaps = (listings) => {
  const disciplineCounts = {};
  const regionCounts = {};
  const providerCounts = {};
  const providerListings = {}; // Track listings per provider for de-duplication
  
  const diverseListings = [];
  
  // Sort by score (highest first)
  const sortedListings = [...listings].sort((a, b) => b.score - a.score);
  
  for (const listing of sortedListings) {
    const publicData = listing?.attributes?.publicData || {};
    const { discipline, region, multiLangAddressParts } = publicData;
    const authorId = listing?.author?.id?.uuid;
    
    // Get region from multiLangAddressParts if available
    const listingRegion = region || multiLangAddressParts?.english?.region || 'unknown';
    
    // Check discipline cap
    const disciplineCount = disciplineCounts[discipline] || 0;
    if (discipline && disciplineCount >= DIVERSITY_CAPS.MAX_PER_DISCIPLINE) {
      continue;
    }
    
    // Check region cap
    const regionCount = regionCounts[listingRegion] || 0;
    if (regionCount >= DIVERSITY_CAPS.MAX_PER_REGION) {
      continue;
    }
    
    // Check provider cap
    const providerCount = providerCounts[authorId] || 0;
    if (authorId && providerCount >= DIVERSITY_CAPS.MAX_PER_PROVIDER) {
      continue;
    }
    
    // Check for near-identical listings from same provider
    if (authorId && providerListings[authorId]) {
      const existingListings = providerListings[authorId];
      const isDuplicate = existingListings.some(existing => {
        // Check if listings are too similar (same discipline, similar title)
        const sameActivity = existing.attributes?.publicData?.activity === publicData?.activity;
        const sameDiscipline = existing.attributes?.publicData?.discipline === discipline;
        const titleSimilarity = calculateTitleSimilarity(
          existing.attributes?.title || '',
          listing.attributes?.title || ''
        );
        
        return sameActivity && sameDiscipline && titleSimilarity > 0.7;
      });
      
      if (isDuplicate) continue;
    }
    
    // Add to diverse listings
    diverseListings.push(listing);
    
    // Update counts
    if (discipline) {
      disciplineCounts[discipline] = disciplineCount + 1;
    }
    regionCounts[listingRegion] = regionCount + 1;
    if (authorId) {
      providerCounts[authorId] = providerCount + 1;
      if (!providerListings[authorId]) {
        providerListings[authorId] = [];
      }
      providerListings[authorId].push(listing);
    }
  }
  
  return diverseListings;
};

/**
 * Calculate similarity between two titles (simple word overlap)
 * @param {string} title1
 * @param {string} title2
 * @returns {number} - Similarity score from 0 to 1
 */
const calculateTitleSimilarity = (title1, title2) => {
  const words1 = title1.toLowerCase().split(/\s+/).filter(w => w.length > 3);
  const words2 = title2.toLowerCase().split(/\s+/).filter(w => w.length > 3);
  
  if (words1.length === 0 || words2.length === 0) return 0;
  
  const commonWords = words1.filter(w => words2.includes(w));
  return (2 * commonWords.length) / (words1.length + words2.length);
};

/**
 * Score and filter listings for landing page carousel
 * @param {Array} listings - Raw listings from API
 * @param {Object} options - Options including upcomingSessionsMap
 * @returns {Array} - Top scored and diverse listings
 */
export const scoreAndFilterListings = (listings, options = {}) => {
  const { upcomingSessionsMap = {}, limit = 10 } = options;
  
  // Calculate score for each listing
  const scoredListings = listings.map(listing => {
    const listingId = listing?.id?.uuid;
    const upcomingSessions = upcomingSessionsMap[listingId] || [];
    const score = calculateListingScore(listing, upcomingSessions);
    
    return {
      ...listing,
      score,
    };
  });
  
  // Apply diversity caps
  const diverseListings = applyDiversityCaps(scoredListings);
  
  // Return top N
  return diverseListings.slice(0, limit);
};

/**
 * Log scoring details for debugging
 * @param {Object} listing - Listing object
 * @param {Array} upcomingSessions - Array of upcoming sessions
 */
export const logScoringDetails = (listing, upcomingSessions = []) => {
  const title = listing?.attributes?.title || 'Unknown';
  const discipline = listing?.attributes?.publicData?.discipline || 'Unknown';
  const region = listing?.attributes?.publicData?.region || 
                 listing?.attributes?.publicData?.multiLangAddressParts?.english?.region || 
                 'Unknown';
  
  console.log(`\n📊 Scoring for: ${title}`);
  console.log(`   Discipline: ${discipline}, Region: ${region}`);
  console.log(`   Seasonal:     ${calculateSeasonalScore(listing).toFixed(1)}`);
  console.log(`   Rating:       ${calculateRatingScore(listing).toFixed(1)}`);
  console.log(`   Completeness: ${calculateCompletenessScore(listing).toFixed(1)}`);
  console.log(`   Availability: ${calculateAvailabilityScore(listing, upcomingSessions).toFixed(1)}`);
  console.log(`   Recency:      ${calculateRecencyScore(listing).toFixed(1)}`);
  console.log(`   Images:       ${calculateImageScore(listing).toFixed(1)}`);
  console.log(`   TOTAL:        ${calculateListingScore(listing, upcomingSessions).toFixed(1)}`);
};

const listingScoring = {
  calculateListingScore,
  scoreAndFilterListings,
  applyDiversityCaps,
  getCurrentSeason,
  getRelevantSeasons,
  logScoringDetails,
  DIVERSITY_CAPS,
  WEIGHTS,
  DISCIPLINE_SEASONS,
};

export default listingScoring;
